import { runtimeConfig } from "@/runtime-config";
import {
  Features,
  OnlyFeaturesState,
  selectHasFeature,
} from "@/store/features/features-slice";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SettingsApiTypes } from "@stellar/settings-api-client";

type ThemeState = {
  /** The `theme_mode` user setting, undefined if the user never set it */
  themeMode?: SettingsApiTypes.SupportedThemeModes;
  /** True once the initial `theme_mode` request has settled, so local changes can't be overwritten by it */
  isThemeModeLoaded: boolean;
};

type OnlyThemeState = { theme: ThemeState };

const initialState: Readonly<ThemeState> = Object.freeze({
  isThemeModeLoaded: false,
});

const themeSlice = createSlice({
  initialState,
  name: "theme",
  reducers: {
    /**
     * @param state of this slice
     * @param action the new theme mode, or undefined to reset it
     */
    setThemeMode(
      state,
      action: PayloadAction<SettingsApiTypes.SupportedThemeModes | undefined>,
    ) {
      state.themeMode = action.payload;
    },
    /**
     * Mark the setting as loading, so local changes are blocked until the new request settles
     *
     * @param state of this slice
     */
    themeModeLoading(state) {
      state.isThemeModeLoaded = false;
    },
    /**
     * @param state of this slice
     * @param action the loaded theme mode, or undefined if the user never set it or loading failed
     */
    themeModeLoaded(
      state,
      action: PayloadAction<SettingsApiTypes.SupportedThemeModes | undefined>,
    ) {
      state.themeMode = action.payload;
      state.isThemeModeLoaded = true;
    },
  },
});

export const { setThemeMode, themeModeLoading, themeModeLoaded } =
  themeSlice.actions;

export const themeReducer = themeSlice.reducer;

/**
 * @param state the root application state
 * @returns true if the `theme_mode` setting is applied: only with the DarkMode feature, and never on prod,
 * which always use the light theme while dark mode is work in progress
 */
export function selectIsThemeModeEnabled(state: OnlyFeaturesState): boolean {
  return (
    !runtimeConfig.deployment.environment.includes("prod") &&
    selectHasFeature(Features.DarkMode)(state)
  );
}

/**
 * @param state the root application state
 * @returns the theme mode to apply: light unless the theme mode is enabled and the user explicitly chose dark or system
 */
export function selectThemeMode(
  state: OnlyThemeState & OnlyFeaturesState,
): SettingsApiTypes.SupportedThemeModes {
  if (!selectIsThemeModeEnabled(state)) return "light";
  return state.theme.themeMode ?? "light";
}

/**
 * @param state the root application state
 * @returns true once the `theme_mode` user setting has been loaded
 */
export function selectIsThemeModeLoaded({ theme }: OnlyThemeState): boolean {
  return theme.isThemeModeLoaded;
}
